import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Input,
  InputGroup, InputLeftElement, Icon, Spinner, Flex, VStack, HStack,
} from '@chakra-ui/react';
import { FaSearch, FaUsers } from 'react-icons/fa';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';

export default function FacilityPatients() {
  const { facilityId } = useOutletContext();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchPatients(); }, [facilityId]); // eslint-disable-line

  const fetchPatients = async () => {
    try {
      const { data } = await selectAll(() => supabase
        .from('submissions')
        .select('patient_name, patient_email, patient_dob, patient_gender, status, submitted_at')
        .eq('facility_id', facilityId)
        .order('submitted_at', { ascending: false }));

      // Deduplicate by patient name
      const patientMap = {};
      (data || []).forEach(sub => {
        const key = (sub.patient_name || '').toLowerCase().trim();
        if (!key) return;
        if (!patientMap[key]) {
          patientMap[key] = {
            name: sub.patient_name,
            email: sub.patient_email,
            dob: sub.patient_dob,
            gender: sub.patient_gender,
            draws: 0,
            lastDraw: sub.submitted_at,
            lastStatus: sub.status,
          };
        }
        patientMap[key].draws++;
        if (sub.submitted_at > patientMap[key].lastDraw) {
          patientMap[key].lastDraw = sub.submitted_at;
          patientMap[key].lastStatus = sub.status;
        }
      });
      setPatients(Object.values(patientMap).sort((a, b) => (b.lastDraw || '').localeCompare(a.lastDraw || '')));
    } catch (err) {
      console.error('Fetch patients error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = patients.filter(p =>
    !search ||
    (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Heading size="lg" fontWeight="700" mb={1}>Patients</Heading>
      <Text color="gray.500" fontSize="sm" mb={6}>All patients from your facility</Text>

      <HStack spacing={4} mb={4}>
        <InputGroup maxW="350px">
          <InputLeftElement><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
          <Input placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} bg="white" />
        </InputGroup>
        <Badge colorScheme="teal" fontSize="sm" px={3} py={1} borderRadius="full">{filtered.length} patients</Badge>
      </HStack>

      {loading ? (
        <Flex py={10} justify="center"><Spinner size="lg" /></Flex>
      ) : filtered.length === 0 ? (
        <VStack py={10} spacing={3}>
          <Icon as={FaUsers} boxSize={12} color="gray.300" />
          <Text color="gray.500">{search ? 'No patients match your search' : 'No patients yet. Create a blood draw to add patients.'}</Text>
        </VStack>
      ) : (
        <Box overflowX="auto" bg="white" borderRadius="xl" shadow="sm">
          <Table size="sm">
            <Thead>
              <Tr bg="gray.50">
                <Th py={3}>Patient Name</Th>
                <Th py={3}>Email</Th>
                <Th py={3}>DOB</Th>
                <Th py={3}>Gender</Th>
                <Th py={3} isNumeric>Total Draws</Th>
                <Th py={3}>Last Draw</Th>
                <Th py={3}>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((p, i) => (
                <Tr key={i} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="500" fontSize="sm">{p.name}</Td>
                  <Td fontSize="sm" color="gray.600">{p.email || '—'}</Td>
                  <Td fontSize="sm" color="gray.600">{p.dob || '—'}</Td>
                  <Td fontSize="sm" color="gray.600">{p.gender || '—'}</Td>
                  <Td isNumeric fontWeight="600">{p.draws}</Td>
                  <Td fontSize="sm" color="gray.500">{p.lastDraw ? new Date(p.lastDraw).toLocaleDateString() : '—'}</Td>
                  <Td><Badge colorScheme={p.lastStatus === 'completed' ? 'green' : 'orange'} fontSize="xs">{(p.lastStatus || 'pending').replace(/_/g, ' ')}</Badge></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
